import { getAnonId } from '../lib/tracking';

export const BLACK_OPS_LEAD_CAPTURE_URL =
  'https://asbctbwxfoosmfehewwl.supabase.co/functions/v1/public-lead-capture';

const LS_SESSION_COUNT = 'ctc_session_count';
const SS_SESSION_COUNTED = 'ctc_session_counted';
const SS_LANDING_PAGE = 'ctc_landing_page';
const SS_PAGES_VIEWED = 'ctc_pages_viewed';

export interface AttributionSnapshot {
  referrer_page?: string;
  referrer_path?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  original_referrer?: string;
  landing_page?: string;
  session_count?: number;
  pages_viewed?: number;
}

export interface LeadCapturePayload extends AttributionSnapshot {
  form_type: 'contact' | 'quote_request' | 'newsletter' | 'calculator';
  form_id: string;
  email: string;
  /** Cloudflare Turnstile token, verified server-side by the edge function. */
  turnstile_token: string;
  anon_id?: string;
  first_name?: string;
  last_name?: string;
  phone?: string;
  company_name?: string;
  message?: string;
  product_interest?: string;
  product_interests?: string;
  source?: string;
  calculator_data?: Record<string, unknown>;
  extra_fields?: Record<string, unknown>;
}

export interface BlackOpsResponse {
  success: boolean;
  lead_id?: string;
  subscriber_id?: string;
  error?: string;
}

function safeWindow(): boolean {
  return typeof window !== 'undefined';
}

export function captureAttribution(): AttributionSnapshot {
  if (!safeWindow()) return {};

  const snap: AttributionSnapshot = {};

  try {
    snap.referrer_page = window.location.href;
    snap.referrer_path = window.location.pathname;

    const params = new URLSearchParams(window.location.search);
    const utmKeys: (keyof AttributionSnapshot)[] = [
      'utm_source',
      'utm_medium',
      'utm_campaign',
      'utm_term',
      'utm_content',
    ];
    utmKeys.forEach((k) => {
      const v = params.get(k);
      if (v) (snap as Record<string, string | number | undefined>)[k] = v;
    });

    const ref = document.referrer;
    if (ref && !ref.startsWith(window.location.origin)) {
      snap.original_referrer = ref;
    }

    let landing = sessionStorage.getItem(SS_LANDING_PAGE);
    if (!landing) {
      landing = window.location.href;
      sessionStorage.setItem(SS_LANDING_PAGE, landing);
    }
    snap.landing_page = landing;

    const counted = sessionStorage.getItem(SS_SESSION_COUNTED);
    let sessionCount = parseInt(localStorage.getItem(LS_SESSION_COUNT) || '0', 10);
    if (!counted) {
      sessionCount = sessionCount > 0 ? sessionCount + 1 : 1;
      localStorage.setItem(LS_SESSION_COUNT, String(sessionCount));
      sessionStorage.setItem(SS_SESSION_COUNTED, '1');
    } else {
      if (sessionCount < 1) sessionCount = 1;
    }
    snap.session_count = sessionCount;

    const pv = parseInt(sessionStorage.getItem(SS_PAGES_VIEWED) || '0', 10);
    snap.pages_viewed = pv > 0 ? pv : 1;
  } catch {
    // Storage may be unavailable (private mode, etc.) — return what we have.
  }

  return snap;
}

export function recordPageView(): void {
  if (!safeWindow()) return;
  try {
    if (!sessionStorage.getItem(SS_LANDING_PAGE)) {
      sessionStorage.setItem(SS_LANDING_PAGE, window.location.href);
    }
    const pv = parseInt(sessionStorage.getItem(SS_PAGES_VIEWED) || '0', 10);
    sessionStorage.setItem(SS_PAGES_VIEWED, String(pv + 1));
  } catch {
    // ignore
  }
}

export async function submitToBlackOps(
  payload: LeadCapturePayload
): Promise<BlackOpsResponse> {
  try {
    const enrichedPayload = { ...payload, anon_id: payload.anon_id || getAnonId() };
    const res = await fetch(BLACK_OPS_LEAD_CAPTURE_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(enrichedPayload),
    });

    let body: BlackOpsResponse | null = null;
    try {
      body = (await res.json()) as BlackOpsResponse;
    } catch {
      body = null;
    }

    if (!res.ok) {
      return {
        success: false,
        error: body?.error || `Request failed with status ${res.status}`,
      };
    }

    if (!body || body.success === false) {
      return {
        success: false,
        error: body?.error || 'Submission was not accepted',
      };
    }

    return {
      success: true,
      lead_id: body.lead_id,
      subscriber_id: body.subscriber_id,
    };
  } catch (err) {
    return {
      success: false,
      error: err instanceof Error ? err.message : 'Network error',
    };
  }
}
